//@ts-nocheck
import { memo, useMemo, useEffect, useState, useContext } from "react";
import { useWatch, useFormContext } from "react-hook-form";
import toast from "react-hot-toast";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { Modal } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { useDisconnectLoanApplicationAccess, useGetAllSalesAgentUsers, useShareLoanApplication } from "__generated__/apiComponents";
import CommonAccordion from "components/CommonAccordion";
import AuthUserContext from "contexts/session";
import { useSaveForm } from "hooks";
import generateTestId from "utils/generateTestId";
import "./Vehicle.styles.scss";
import { DeleteVehicle, VehicleItem } from "./components";
import fixture from "./fixture";

const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: "background.paper",
    boxShadow: 24,
    pt: 2,
    px: 4,
    pb: 2,
    borderRadius: "10px",
};

const Vehicle = ({ index, remove }: { index: number; remove: any }) => {
    const indexText = (index: number) => {
        switch (index) {
            case 0:
                return "";
            case 1:
                return "2nd";
            case 2:
                return "3rd";
            default:
                return `${index + 1}th`;
        }
    };

    const { setValue, getValues } = useFormContext();

    const vehicle = useWatch({
        name: `requirement.vehicles.${index}`,
    });

    const errors = useWatch({
        name: `errors.requirement.vehicles.${index}`,
    });

    const [selectedAgent, setSelectedAgent] = useState<string>("");

    const firstVehicle = useWatch({
        name: `requirement.vehicles.0`,
    });

    const [selectedOption, setSelectedOption] = useState({
        label: "",
        id: "",
        username: "",
    });

    const [disableSalesAgent, setDisableSalesAgent] = useState<boolean>(false);

    const [open, setOpen] = useState(false);

    const salesAgent = useWatch({
        name: `requirement.vehicles.${index}.salesAgent`,
    });

    const previousSalesAgent = salesAgent?.username;

    const { authUser } = useContext(AuthUserContext);

    const {
        data,
        isLoading: isSalesPersonLoading,
        refetch,
    } = useGetAllSalesAgentUsers(
        { queryParams: { name: salesAgent?.fullName } },
        {
            enabled: false,
            refetchOnWindowFocus: false,

            onError: (error: any) => {
                toast.dismiss();
                toast.error(error?.stack?.message);
            },
        }
    );

    const saveFrom = useSaveForm();

    const { mutate: share } = useShareLoanApplication({
        onError: (error: any) => {
            toast.dismiss();
            toast.error(error?.response?.data?.message);
        },
        onSuccess: (data: any) => {
            toast.dismiss();
            toast.success(data?.message);
            setValue(`requirement.vehicles.0.salesAgent.username`, selectedOption?.username);
            setValue(`requirement.vehicles.0.salesAgent.fullName`, selectedOption?.label);

            saveFrom();
        },
    });

    const { mutate: disconnect } = useDisconnectLoanApplicationAccess({
        onError: (error: any) => {
            toast.dismiss();
            toast.error(error?.stack?.message);
        },
    });

    useEffect(() => {
        if (index === 0) {
            const delayDebounceFn = setTimeout(() => {
                if (salesAgent?.fullName.length > 2) {
                    refetch();
                }
            }, 300);

            return () => clearTimeout(delayDebounceFn);
        }
    }, [salesAgent?.fullName]);

    const salesAgentNames = useMemo(() => {
        if (data?.length > 0) {
            return data?.map((item: any, index: number) => {
                return {
                    label: item?.full_name,
                    id: item?.id,
                    username: item?.username,
                };
            });
        } else {
            return [];
        }
    }, [data]);

    const handleSelectValue = (value: string) => {
        if (value.length > 0) {
            setSelectedAgent(value);
            setOpen(true);
        }
    };

    useEffect(() => {
        if (localStorage.getItem("disable_form") !== "true") {
            if (selectedOption?.label !== "" && selectedOption?.id !== "") {
                if (previousSalesAgent)
                    disconnect({
                        body: { loanApplicationId: localStorage.getItem("form_id") ?? "", username: previousSalesAgent },
                    });
                share({
                    body: { loanApplicationId: localStorage.getItem("form_id") ?? "", userId: selectedOption?.id },
                });
            }
        }
    }, [selectedOption]);

    const handleSendEmail = () => {
        const user = salesAgentNames.find((item: any) => item.label === selectedAgent);
        setSelectedOption((prevUser: any) => {
            return prevUser === user ? prevUser : user;
        });
    };

    useEffect(() => {
        if (index > 0) {
            setValue(`requirement.vehicles.${index}.salesAgent.fullName`, firstVehicle?.salesAgent?.fullName);
            setValue(`requirement.vehicles.${index}.salesAgent.username`, firstVehicle?.salesAgent?.username);
            setValue(`requirement.vehicles.${index}.sellerName`, firstVehicle?.sellerName);
        }
    }, [firstVehicle]);

    useEffect(() => {
        if (authUser && authUser?.role === "salesperson" && index === 0) {
            setValue(`requirement.vehicles.0.salesAgent.fullName`, authUser?.full_name);
            setValue(`requirement.vehicles.0.salesAgent.username`, authUser?.username);
        }
    }, [authUser]);

    useEffect(() => {
        if (firstVehicle?.salesAgent?.fullName === "") {
            setValue(`requirement.vehicles.0.salesAgent.username`, "");
        }
    }, []);

    const activeFields: string[] = [];

    useEffect(() => {
        let errorsCounts = 0;
        for (const item of activeFields) {
            if (getValues(`requirement.vehicles.${index}.${item}`) === "" || getValues(`requirement.vehicles.${index}.${item}`) === undefined) {
                errorsCounts++;
            }
        }
        setValue(`errors.requirement.vehicles.${index}.errorsCounts`, errorsCounts / 2);
    }, [vehicle]);

    return (
        <Grid className="equipment-tab" container direction="row" justifyContent="flex-start" alignItems="flex-start">
            <CommonAccordion
                title={`${indexText(index)} Vehicle`}
                tab="requirement"
                showError={errors?.errorsCounts > 0}
                id="vehicle__container"
                data-testid={generateTestId(`${indexText(index)} Vehicle`, "accordion")}
            >
                {fixture.map((props, internalIndex) => (
                    <VehicleItem
                        index={index}
                        key={internalIndex}
                        props={props}
                        isSalesPersonLoading={isSalesPersonLoading}
                        salesAgentNames={salesAgentNames}
                        disableSalesAgent={disableSalesAgent}
                        handleSelectValue={handleSelectValue}
                        activeFields={activeFields}
                        isSalesPerson={authUser?.role === "salesperson" || (firstVehicle?.salesAgent?.username !== "" && !authUser?.isAdmin)}
                    />
                ))}
            </CommonAccordion>
            <DeleteVehicle index={index} remove={remove} />
            {index === 0 && (
                <Modal hideBackdrop open={open} onClose={() => setOpen(false)}>
                    <Box sx={{ ...style, width: 200 }}>
                        <Grid container justifyContent="center" alignItems="center">
                            <Grid item xs={12} container justifyContent={"flex-end"} alignItems="center">
                                <CloseOutlinedIcon onClick={() => setOpen(false)} className="close-icon" />
                            </Grid>
                            <Grid item xs={12} container justifyContent={"center"} alignItems="center">
                                <Typography className="otp-text">
                                    sales agent selection cannot be changed once made. Do you want to confirm your choice?
                                </Typography>
                            </Grid>
                            <Grid item xs={12} container justifyContent={"center"} alignItems="center">
                                <Typography className="otp-text">{selectedAgent}</Typography>
                            </Grid>
                            <Grid item xs={12} container alignItems="center" justifyContent="space-around">
                                <Button
                                    variant="contained"
                                    className="btn"
                                    onClick={() => {
                                        handleSendEmail();
                                        setOpen(false);
                                    }}
                                >
                                    Yes
                                </Button>
                                <Button
                                    style={{ backgroundColor: "#767676", marginLeft: "10px" }}
                                    variant="contained"
                                    className="btn"
                                    onClick={() => {
                                        setOpen(false);
                                    }}
                                >
                                    No
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                </Modal>
            )}
        </Grid>
    );
};

export default memo(Vehicle);
